import { motion, AnimatePresence, useScroll, useTransform, useAnimation, useInView } from "framer-motion";
import { FiArrowUp, FiX, FiExternalLink } from "react-icons/fi";
import { useRef, useState, useEffect } from "react";
import { fastItems, slowItems, defaultItems } from "./items";

const ColumnItem = ({ item, openOverlay, heightClass, imgHeightClass }) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.6, ease: "easeOut" }
        }
      }}
      className={`${heightClass} relative mb-2 rounded-lg overflow-hidden group`}
    >
      <button 
        onClick={() => openOverlay(item)}
        className="w-full h-full focus:outline-none"
      >
        <img
          alt={item.title}
          className={`w-full ${imgHeightClass} object-cover transition-transform duration-300 bg-white group-hover:scale-105`}
          src={item.img}
        />
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="inline-block bg-white/90 rounded-lg px-3 py-2 mb-2">
          <h3 className="text-xl text-black">{item.title}</h3>
        </div>
        <p className="text-white rounded-lg p-3l;">
          {item.description}
        </p>
      </div>
    </motion.div>
  );
};

const ThreeColScrolly = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeColumn, setActiveColumn] = useState("about");
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredColumn, setHoveredColumn] = useState(null);

  // Set up transforms for each column
  const aboutTransform = useTransform(scrollYProgress, [0, 1], ['0%', '-85%']);
  const experienceTransform = useTransform(scrollYProgress, [0, 1], ['0%', '-50%']);
  const projectsTransform = useTransform(scrollYProgress, [0, 1], ['0%', '-35%']);

  // Check for mobile on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openOverlay = (item) => {
    setSelectedItem(item);
  };

  const closeOverlay = () => {
    setSelectedItem(null);
  };

  const handleColumnHover = (column) => {
    if (!isMobile) {
      setHoveredColumn(column);
    }
  };

  const handleColumnLeave = () => {
    setHoveredColumn(null);
  };

  // Get brightness class based on hover state
  const getBrightnessClass = (column) => {
    if (isMobile || !hoveredColumn) return 'brightness-100';
    return hoveredColumn === column ? 'brightness-100' : 'brightness-50';
  };

  return (
    <>
      <section className="relative bg-black min-h-screen">
        {/* Mobile Navigation */}
        {isMobile && (
          <div className="sticky top-0 z-20 flex text-white h-16 items-center px-4 bg-black">
            <button 
              onClick={() => setActiveColumn("about")}
              className={`flex-1 text-center ${activeColumn === "about" ? "text-white font-bold" : "text-gray-400"}`}
            >
              About
            </button>
            <button 
              onClick={() => setActiveColumn("experience")}
              className={`flex-1 text-center ${activeColumn === "experience" ? "text-white font-bold" : "text-gray-400"}`}
            >
              Experience
            </button>
            <button 
              onClick={() => setActiveColumn("projects")}
              className={`flex-1 text-center ${activeColumn === "projects" ? "text-white font-bold" : "text-gray-400"}`}
            >
              Projects
            </button>
          </div>
        )}

        {/* Desktop Titles */}
        {!isMobile && (
          <div className="sticky top-0 z-10 flex text-white h-16 items-center px-4 bg-black">
            <div className="w-1/3">
              <h2 className="text-xl font-bold">Joshua Law</h2>
            </div>
            <div className="w-1/3">
              <h2 className="text-xl font-bold">Experience</h2>
            </div>
            <div className="w-1/3">
              <h2 className="text-xl font-bold">Projects</h2>
            </div>
          </div>
        )}

        {/* Columns Container */}
        <section ref={targetRef} className="flex bg-black text-white min-h-screen gap-2 px-2 pt-2 pb-16">
          {/* About Column */}
          <div 
            className={`${isMobile ? (activeColumn === "about" ? "block" : "hidden") : "block"} w-full md:w-1/3 h-full overflow-hidden sticky top-16 transition-all duration-300 ${getBrightnessClass('about')}`}
            onMouseEnter={() => handleColumnHover('about')}
            onMouseLeave={handleColumnLeave}
          >
            <motion.div 
              style={{ y: isMobile ? '0%' : aboutTransform }} 
              className="h-full flex flex-col gap-2"
            >
              {fastItems.map((item) => (
                <ColumnItem 
                  key={item.id}
                  item={item}
                  openOverlay={openOverlay}
                  heightClass="h-[75vh]"
                  imgHeightClass="h-[75%]"
                />
              ))}
            </motion.div>
          </div>

          {/* Experience Column */}
          <div 
            className={`${isMobile ? (activeColumn === "experience" ? "block" : "hidden") : "block"} w-full md:w-1/3 h-full overflow-hidden sticky top-16 transition-all duration-300 ${getBrightnessClass('experience')}`}
            onMouseEnter={() => handleColumnHover('experience')}
            onMouseLeave={handleColumnLeave}
          >
            <motion.div 
              style={{ y: isMobile ? '0%' : experienceTransform }} 
              className="h-full flex flex-col gap-2"
            >
              {slowItems.map((item) => (
                <ColumnItem 
                  key={item.id}
                  item={item}
                  openOverlay={openOverlay}
                  heightClass="h-[75vh]"
                  imgHeightClass="h-[75%]"
                />
              ))}
            </motion.div>
          </div>

          {/* Projects Column */}
          <div 
            className={`${isMobile ? (activeColumn === "projects" ? "block" : "hidden") : "block"} w-full md:w-1/3 h-full overflow-hidden sticky top-16 transition-all duration-300 ${getBrightnessClass('projects')}`}
            onMouseEnter={() => handleColumnHover('projects')}
            onMouseLeave={handleColumnLeave}
          >
            <motion.div 
              style={{ y: isMobile ? '0%' : projectsTransform }} 
              className="h-full flex flex-col gap-2"
            >
              {defaultItems.map((item) => (
                <ColumnItem 
                  key={item.id}
                  item={item}
                  openOverlay={openOverlay}
                  heightClass="h-[75vh]"
                  imgHeightClass="h-[75%]"
                />
              ))}
            </motion.div>
          </div>
        </section>
      </section>

      {/* Overlay */}
      <AnimatePresence>
        {selectedItem && (
          <>
            {/* Backdrop - Now closes overlay when clicked on both mobile and desktop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
              className="fixed inset-0 z-50 bg-black bg-opacity-50"
            />
            
            {/* Overlay Content */}
            {isMobile ? (
              // Mobile - Full screen overlay
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed bottom-0 left-0 right-0 h-[90vh] z-50 bg-black text-white shadow-xl flex flex-col rounded-t-3xl overflow-hidden"
                onClick={(e) => e.stopPropagation()} // Prevent click from reaching backdrop
              >
                <div className="flex-1 overflow-y-auto p-6">
                  <button 
                    onClick={closeOverlay}
                    className="absolute top-4 right-4 text-2xl hover:text-gray-400 transition-colors"
                  >
                    <FiX />
                  </button>
                  
                  <img 
                    src={selectedItem.img} 
                    alt={selectedItem.title} 
                    className="w-full h-64 object-cover bg-white rounded-lg mb-4"
                  />
                  
                  <h2 className="text-2xl font-bold mb-2">{selectedItem.title}</h2>
                  <p className="text-gray-300 mb-4">{selectedItem.description}</p>
                  
                  <div 
                    className="prose prose-invert mb-6"
                    dangerouslySetInnerHTML={{ __html: selectedItem.details }}
                  />
                </div>

                {selectedItem.link && (
                  <div className="p-6 border-t border-gray-800">
                    <a
                      href={selectedItem.link}
                      className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      <span>View Project Details</span>
                      <FiExternalLink className="text-lg" />
                    </a>
                  </div>
                )}
              </motion.div>
            ) : (
              // Desktop - Half page overlay from right
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: "0%" }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed top-0 right-0 w-1/2 h-full z-50 bg-black text-white shadow-xl flex flex-col overflow-hidden border-l border-gray-800"
                onClick={(e) => e.stopPropagation()} // Prevent click from reaching backdrop
              >
                <div className="flex-1 overflow-y-auto p-6">
                  <button 
                    onClick={closeOverlay}
                    className="absolute top-4 right-4 text-2xl hover:text-gray-400 transition-colors"
                  >
                    <FiX />
                  </button>
                  
                  <img 
                    src={selectedItem.img} 
                    alt={selectedItem.title} 
                    className="w-full h-64 object-cover rounded-lg mb-4"
                  />
                  
                  <h2 className="text-2xl font-bold mb-2">{selectedItem.title}</h2>
                  <p className="text-gray-300 mb-4">{selectedItem.description}</p>
                  
                  <div 
                    className="prose prose-invert mb-6"
                    dangerouslySetInnerHTML={{ __html: selectedItem.details }}
                  />
                </div>

                {selectedItem.link && (
                  <div className="p-6 border-t border-gray-800">
                    <a
                      href={selectedItem.link}
                      className="flex items-center justify-center gap-2 w-full bg-violet-600 hover:bg-violet-500   text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      <span>Learn More</span>
                      <FiExternalLink className="text-lg" />
                    </a>
                  </div>
                )}
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>

      <div className="bg-black text-white p-4 grid place-items-center">
        <FiArrowUp className="text-xl" />
      </div>
    </>
  );
};

export default ThreeColScrolly;